// src/utils/createOrder.js

/**
 * Region where your CF is deployed.
 * Keep in sync with your Firebase Functions region.
 */
const REGION = "europe-west10";

/**
 * While testing you can force prod CF even from localhost.
 */
const USE_PROD = true;

/**
 * Build the HTTPS endpoint for the createOrder callable HTTP function.
 * @param {string} projectId
 */
const getCreateOrderEndpoint = (projectId) => {
  const prod = `https://${REGION}-${projectId}.cloudfunctions.net/createOrder`;
  if (USE_PROD) return prod;

  const isLocal =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  return isLocal
    ? `http://127.0.0.1:5001/${projectId}/${REGION}/createOrder`
    : prod;
};

/**
 * Safe number parser that accepts number/string/"12,34"/null.
 * @param {any} v
 * @returns {number}
 */
export const toNumber = (v) => {
  if (v == null) return 0;
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const n = parseFloat(String(v).replace(",", ".").trim());
  return Number.isFinite(n) ? n : 0;
};

/**
 * Minimal network wrapper with timeout.
 * @param {RequestInfo} url
 * @param {RequestInit & { timeoutMs?: number }} options
 */
async function fetchJson(url, { timeoutMs = 15000, ...init } = {}) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);

  try {
    const res = await fetch(url, { ...init, signal: ctrl.signal });
    let data = null;
    try {
      data = await res.json();
    } catch {
      // response may be empty or not JSON
      data = null;
    }

    if (!res.ok) {
      const message =
        (data && (data.error || data.message)) ||
        `HTTP ${res.status} ${res.statusText}`;
      throw new Error(message);
    }
    return data;
  } catch (err) {
    if (err?.name === "AbortError") {
      throw new Error("Network timeout while creating order");
    }
    throw err;
  } finally {
    clearTimeout(t);
  }
}

/**
 * Create order on the server (writes to Firestore in CF).
 *
 * @param {Object} params
 * @param {string} params.projectId - Firebase project id
 * @param {string} params.shopId
 * @param {Array<{
 *   id:string,
 *   size:string,
 *   quantity:number,
 *   ingredientIds:string[],
 *   extraIds:string[]
 * }>} params.cartItems - minimal pricing shape
 * @param {Object} params.orderMeta - method, contact, address, notes, payment, cartItems(sanitized), orderId, userId, deviceInfo, placedBy, source
 * @returns {Promise<{ ok: true, id: string, totals?: any }>}
 */
export async function createOrder({ projectId, shopId, cartItems, orderMeta }) {
  if (!projectId) throw new Error("createOrder: projectId is required");
  if (!shopId) throw new Error("createOrder: shopId is required");
  if (!Array.isArray(cartItems) || cartItems.length === 0)
    throw new Error("createOrder: cartItems cannot be empty");

  const endpoint = getCreateOrderEndpoint(projectId);

  const payload = {
    shopId,
    items: cartItems,
    orderMeta,
  };

  const data = await fetchJson(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    timeoutMs: 20000,
    body: JSON.stringify(payload),
  });

  if (!data?.ok || !data?.id) {
    const msg = data?.error || "Unexpected response from createOrder";
    throw new Error(msg);
  }

  return data; // { ok: true, id, totals? }
}

/**
 * Normalize a cart array to the minimal pricing shape for the backend.
 * Only SELECTED ingredients/extras are sent.
 *
 * @param {Array<any>} cartItems
 * @returns {Array<{
 *  id:string,
 *  size:string,
 *  quantity:number,
 *  ingredientIds:string[],
 *  extraIds:string[],
 *  // legacy single field kept for compatibility (optional on server):
 *  extraId?:string
 * }>}
 */
export const mapCartForServer = (cartItems) =>
  (cartItems || []).map((c) => {
    // ingredients -> string IDs
    const ingredientIds = Array.isArray(c.selectedIngredients)
      ? c.selectedIngredients
          .map((x) => (typeof x === "string" ? x : x?.id))
          .filter(Boolean)
      : [];

    // extras -> string IDs; supports selectedExtras[] or single selectedExtra
    const extraIds = Array.isArray(c.selectedExtras)
      ? c.selectedExtras
          .map((x) => (typeof x === "string" ? x : x?.id))
          .filter(Boolean)
      : c.selectedExtra
      ? [c.selectedExtra]
      : [];

    return {
      id: c.id,
      size: c.selectedSize || "",
      quantity: Number(c.quantity) || 0,
      ingredientIds,
      extraIds,
      // keep for legacy CFs that still read a single extra
      extraId: extraIds[0] || "",
    };
  });
